import {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type RawAxiosRequestHeaders,
  type ResponseType
} from "axios";
import { HttpHeaderKey } from "common/enums";
import { ApiResult } from "common/models";
import Axios from "./http-interceptor";

export abstract class BaseHTTPClient {
  //private _axiosInstance: AxiosInstance;

  // constructor(instance: AxiosInstance) {
  //   this._axiosInstance = instance;
  // }

  protected static async get<T>(
    url: string,
    contextHeader: RawAxiosRequestHeaders = {},
    responseType?: ResponseType
  ): Promise<ApiResult<T>> {
    let result: ApiResult<T>;
    const axiosOpts: AxiosRequestConfig = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...contextHeader
      },
      responseType
    };
    try {
      const axiosResult = await Axios.get(url, axiosOpts);
      result = new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      return this.createErrorResponse(error as AxiosError);
    }
    return result;
  }

  protected static async post<T>(
    url: string,
    data: any,
    contextHeader: RawAxiosRequestHeaders = {}
  ): Promise<ApiResult<T>> {
    let result: ApiResult<T>;

    // For FormData, don't set Content-Type header - let browser handle it
    const defaultHeaders =
      data instanceof FormData
        ? {}
        : { [HttpHeaderKey.CONTENT_TYPE]: "application/json" };

    const axiosOpts: AxiosRequestConfig = {
      headers: {
        ...defaultHeaders,
        ...contextHeader
      }
    };

    try {
      const axiosResult = await Axios.post(url, data, axiosOpts);
      result = new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      return this.createErrorResponse(error as AxiosError);
    }

    return result;
  }

  protected static async postWithTimeout<T>(
    url: string,
    data: any,
    contextHeader: RawAxiosRequestHeaders = {},
    timeout = 900000
  ): Promise<ApiResult<T>> {
    let result: ApiResult<T>;

    const defaultHeaders =
      data instanceof FormData
        ? {}
        : { [HttpHeaderKey.CONTENT_TYPE]: "application/json" };

    const axiosOpts: AxiosRequestConfig = {
      headers: {
        ...defaultHeaders,
        ...contextHeader
      },
      timeout
    };

    try {
      const axiosResult = await Axios.post(url, data, axiosOpts);
      result = new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      return this.createErrorResponse(error as AxiosError);
    }

    return result;
  }

  protected static async getWithTimeout<T>(
    url: string,
    contextHeader: RawAxiosRequestHeaders = {},
    timeout = 900000,
    responseType?: ResponseType
  ): Promise<ApiResult<T>> {
    let result: ApiResult<T>;
    const axiosOpts: AxiosRequestConfig = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...contextHeader
      },
      timeout,
      responseType
    };
    try {
      const axiosResult = await Axios.get(url, axiosOpts);
      result = new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      return this.createErrorResponse(error as AxiosError);
    }
    return result;
  }

  protected static async putWithTimeout<T>(
    url: string,
    data: any,
    contextHeader: RawAxiosRequestHeaders = {},
    timeout = 900000
  ): Promise<ApiResult<T>> {
    let result: ApiResult<T>;
    const axiosOpts: AxiosRequestConfig = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...contextHeader
      },
      timeout
    };
    try {
      const axiosResult = await Axios.put(url, data, axiosOpts);
      result = new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      return this.createErrorResponse(error as AxiosError);
    }
    return result;
  }

  protected static async deleteWithTimeout<T>(
    url: string,
    data: any,
    contextHeader: RawAxiosRequestHeaders = {},
    timeout = 900000
  ): Promise<ApiResult<T>> {
    let result: ApiResult<T>;
    const axiosOpts: AxiosRequestConfig = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...contextHeader
      },
      data: data,
      timeout
    };
    try {
      const axiosResult = await Axios.delete(url, axiosOpts);
      result = new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      return this.createErrorResponse(error as AxiosError);
    }
    return result;
  }

  protected static async put<T>(
    url: string,
    data: any,
    contextHeader: RawAxiosRequestHeaders = {}
  ): Promise<ApiResult<T>> {
    let result: ApiResult<T>;
    const axiosOpts: AxiosRequestConfig = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...contextHeader
      }
    };
    try {
      const axiosResult = await Axios.put(url, data, axiosOpts);
      result = new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      return this.createErrorResponse(error as AxiosError);
    }
    return result;
  }

  protected static async delete<T>(
    url: string,
    data: any,
    contextHeader: RawAxiosRequestHeaders = {}
  ): Promise<ApiResult<T>> {
    let result: ApiResult<T>;
    const axiosOpts: AxiosRequestConfig = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...contextHeader
      },
      data: data,
      timeout: 300000
    };
    try {
      const axiosResult = await Axios.delete(url, axiosOpts);
      result = new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      return this.createErrorResponse(error as AxiosError);
    }
    return result;
  }

  protected static async patch<T>(
    url: string,
    data: any,
    contextHeader: RawAxiosRequestHeaders = {}
  ): Promise<ApiResult<T>> {
    let result: ApiResult<T>;
    const axiosOpts: AxiosRequestConfig = {
      headers: {
        [HttpHeaderKey.CONTENT_TYPE]: "application/json",
        ...contextHeader
      }
    };
    try {
      const axiosResult = await Axios.patch(url, data, axiosOpts);
      result = new ApiResult<T>(axiosResult?.data, axiosResult.status);
    } catch (error) {
      return this.createErrorResponse(error as AxiosError);
    }
    return result;
  }

  protected static async getBlob(
    url: string,
    contextHeader: RawAxiosRequestHeaders = {}
  ): Promise<ApiResult<Blob>> {
    let result: ApiResult<Blob>;
    const axiosOpts: AxiosRequestConfig = {
      headers: { ...contextHeader },
      responseType: "blob"
    };
    try {
      const axiosResult = await Axios.get(url, axiosOpts);
      result = new ApiResult<Blob>(axiosResult?.data);
    } catch (error) {
      result = new ApiResult<any>(null);
    }
    return result;
  }

  /**Private methods */
  private static createErrorResponse(axiosError: AxiosError) {
    return new ApiResult<any>(
      axiosError?.response?.data,
      axiosError.response?.status,
      axiosError.message
    );
  }
}
